import { ReactElement, useEffect, useState, useRef } from "react";
import { useLocation, useRoute } from "_/components/router";
import { assocPath } from "ramda";
import { t } from "@lingui/core/macro";
import { useAtom, atom, PrimitiveAtom, useSetAtom } from "jotai";

import { GCodePrintData } from "_/components/view-loaders/gcode/gcode-print";
import { UserAvatar } from "_/components/avatar";
import { Slider } from "_/components/slider";
import { Button } from "_/components/button";
import { MaterialName } from "_/components/materials";
import { ProjectEditForm } from "_/components/project-forms";
import { PermissionTest } from "_/components/permission-test";
import { permissions } from "_/data/rbac/definitions";
import { Modal, ViewerOverlayNotice } from "_/components/modal";
import { ProgressBar } from "_/components/progress-bar";
import { BadUrlError } from "_/components/project-detail";

import { Icon } from "_/components/icon";
import { useUsers } from "_/data/users";
import { useAddToast } from "_/components/toasts";
import {
  Project,
  Revision,
  useCreateOptimization,
  useProjectOptimizations,
  useRevisionMaterials,
} from "_/data/projects";
import { Spinner } from "_/components/spinner";
import { BlobRecord, useBlobContents } from "_/data/blob";

import { viewAtom, type GcodeViewControls } from "_/state/viewer";

import { isValidUuid } from "_/utils";

import { projectRoutes, projectUrls } from "_/routes";

import { StartOptimizationButton } from "_/components/viewer-controls/optimize";
import { OperatorInstructions } from "./operator-instructions";

import * as S from "./styled";

type GCodeControlsProps = {
  /**
   * Jotai atom to access the control state for the G-code visualization.
   */
  controlsAtom: PrimitiveAtom<GcodeViewControls>;

  /** Project (for showing edit settings modal) */
  project: Project;

  /** Revision (needed to populate project info box) */
  revision: Revision;
};

const GCodeControls = ({
  controlsAtom,
  project,
  revision,
}: GCodeControlsProps): ReactElement => {
  const [controls, setControls] = useAtom(controlsAtom);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { mat0, mat1, canOptimize } = useRevisionMaterials(revision);
  const createOptimization = useCreateOptimization(revision);
  const { data: optimizations = [] } = useProjectOptimizations(project.id);
  const revisionOptimization = optimizations.find(
    (o) => o.revisionId === revision.id && !o.job.input.parentOptimizationId
  );
  const optRunning =
    revisionOptimization &&
    ["queued", "pending", "started"].includes(revisionOptimization.job.status);
  const optComplete = revisionOptimization?.job.status === "completed";
  const [_location, setLocation] = useLocation();
  const addToast = useAddToast();

  async function onStartOptimizationJob() {
    try {
      const optimization = await createOptimization.mutateAsync({
        optimizeGcode: canOptimize ?? false,
      });
      setLocation(projectUrls.optimize(project.id, optimization.job.id));
    } catch {
      addToast({
        kind: "danger",
        title: t`components.controls.optimize.start-job-error`,
      });
    }
  }

  const { data: users } = useUsers();

  const owner = users?.find((u) => u.id === project.ownerId);
  const lastModifiedBy = users?.find((u) => u.id === revision.creatorId);

  const { range, value: currentValues } = controls.layer;

  if (!controls) {
    return <></>;
  }

  return (
    <>
      <S.LayerControlContainer>
        <S.SliderLabel>{t`common.layer`}</S.SliderLabel>
        <S.SliderContainer>
          <Slider
            min={0}
            max={range[1]}
            step={1}
            value={currentValues}
            size="large"
            vertical
            tooltipPlacement="before"
            tooltipText={(values) =>
              // Layers in code are 0-based, but our UI is 1-based to match how
              // slicers show layer numbers.
              values.map((v) => `${t`common.layer`}: ${v + 1}`)
            }
            onChange={(values) => {
              setControls((c) => {
                return assocPath(["layer", "value"], values, c);
              });
            }}
          />
        </S.SliderContainer>
      </S.LayerControlContainer>

      <S.RightSideContainer>
        <S.ProjectInfoContainer>
          {owner && (
            <S.ProjectInfoItem>
              <S.ProjectInfoLabel>{t`common.owner`}</S.ProjectInfoLabel>
              <S.ProjectInfoValue>
                <S.UserInfoContainer>
                  <S.UserName>{owner.name}</S.UserName>
                  <UserAvatar size="small" resource={owner} />
                </S.UserInfoContainer>
              </S.ProjectInfoValue>
            </S.ProjectInfoItem>
          )}
          {lastModifiedBy && lastModifiedBy !== owner && (
            <S.ProjectInfoItem>
              <S.ProjectInfoLabel>{t`common.last-modified-by`}</S.ProjectInfoLabel>
              <S.ProjectInfoValue>
                <S.UserInfoContainer>
                  <S.UserName>{lastModifiedBy.name}</S.UserName>
                  <UserAvatar size="small" resource={lastModifiedBy} />
                </S.UserInfoContainer>
              </S.ProjectInfoValue>
            </S.ProjectInfoItem>
          )}
          {mat0 && (
            <S.ProjectInfoItem>
              <S.ProjectInfoLabel>{t`common.primary-material`}</S.ProjectInfoLabel>
              <S.ProjectInfoValue>
                <MaterialName material={mat0} />
              </S.ProjectInfoValue>
            </S.ProjectInfoItem>
          )}
          {mat1 && (
            <S.ProjectInfoItem>
              <S.ProjectInfoLabel>{t`common.secondary-material`}</S.ProjectInfoLabel>
              <S.ProjectInfoValue>
                <MaterialName material={mat1} />
              </S.ProjectInfoValue>
            </S.ProjectInfoItem>
          )}
          {(optRunning || optComplete) && (
            <S.ProjectInfoItem>
              <S.ProjectInfoLabel>{t`components.controls.prepare.optimization`}</S.ProjectInfoLabel>
              <S.OptimizationStatusRow>
                {optComplete ? (
                  <>
                    <S.OptimizationStatusIcon>
                      <Icon variant="CheckCircle" />
                    </S.OptimizationStatusIcon>
                    {t`components.controls.prepare.opt-status-complete`}
                    <S.OptimizationDivider>|</S.OptimizationDivider>
                    <S.ViewOptimizationLink
                      to={projectUrls.optimize(
                        project.id,
                        revisionOptimization.job.id
                      )}
                    >
                      {t`components.controls.prepare.view-optimization`}
                    </S.ViewOptimizationLink>
                  </>
                ) : (
                  <>
                    <Spinner size={16} />
                    {t`components.controls.prepare.opt-status-running`}
                  </>
                )}
              </S.OptimizationStatusRow>
            </S.ProjectInfoItem>
          )}
          <S.ProjectInfoButtonsItem>
            {!optRunning && !optComplete && (
              <StartOptimizationButton
                revision={revision}
                onStartOptimizationJob={() => void onStartOptimizationJob()}
              />
            )}
            <PermissionTest requiredPermissions={permissions.projects.write}>
              <Button kind="secondary" onClick={() => setShowEditModal(true)}>
                {t`common.revise-project`}
              </Button>
            </PermissionTest>
          </S.ProjectInfoButtonsItem>
        </S.ProjectInfoContainer>
        <S.ProjectInfoContainer>
          <OperatorInstructions project={project} revision={revision} />
        </S.ProjectInfoContainer>
      </S.RightSideContainer>
      <Modal
        open={showEditModal}
        title={t`components.project-detail.revise-project`}
        onClose={isUploading ? undefined : () => setShowEditModal(false)}
      >
        <ProjectEditForm
          project={project}
          onComplete={() => setShowEditModal(false)}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
        />
      </Modal>
    </>
  );
};

type GCodeLoaderProps = {
  /**
   * Manifest of the blob to load.
   */
  blobRecord: BlobRecord;

  /** Revision containing this G-code */
  project: Project;

  /** Revision containing this G-code */
  revision: Revision;
};

/**
 * Loads the G-code for the given blob into the viewer.
 */
const GCodeLoader = ({
  blobRecord,
  revision,
  project,
}: GCodeLoaderProps): ReactElement => {
  // Loading progress of the G-code file.
  const [loadProgress, setLoadProgress] = useState(0);
  const [parseProgress, setParseProgress] = useState(0);
  const [constructionProgress, setConstructionProgress] = useState(0);

  const blobId = blobRecord.id;

  // Reset the progress bars when loading a new G-code file.
  useEffect(() => {
    setLoadProgress(0);
    setParseProgress(0);
    setConstructionProgress(0);
  }, [blobId]);

  const {
    data: buffer,
    error: loadingError,
    isFetched,
  } = useBlobContents(blobRecord, setLoadProgress, true);

  const setView = useSetAtom(viewAtom);

  const [controlsAtom, setControlsAtom] =
    useState<PrimitiveAtom<GcodeViewControls>>();

  const [noExtrusion, setNoExtrusion] = useState(false);

  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (isFetched) {
      setLoadProgress(1);
    }
  }, [isFetched]);

  useEffect(() => {
    // clear the controls atom when the data changes so that the controls are reset
    setControlsAtom(undefined);
    if (!buffer) {
      setNoExtrusion(false);
      return undefined;
    }

    const decoder = new TextDecoder("utf-8", { fatal: true });
    const result = decoder.decode(buffer);

    const worker = new Worker(new URL("./gcode.worker.ts", import.meta.url));

    worker.onmessage = (event: {
      data: {
        parseProgress?: number;
        constructionProgress?: number;
        printData?: GCodePrintData;
      };
    }) => {
      if (event.data.parseProgress) {
        setParseProgress(event.data.parseProgress);
      } else if (event.data.constructionProgress) {
        setConstructionProgress(event.data.constructionProgress);
      } else if (event.data.printData) {
        const printData = event.data.printData;

        // Check if the parsed G-code contains extrusion commands.
        // If not, display a message to the user.
        if (printData.totalSegments <= 0) {
          return setNoExtrusion(true);
        }

        const topLayerIndex = printData.topLayer;

        const cAtom = atom<GcodeViewControls>({
          layer: { range: [0, topLayerIndex], value: [0, topLayerIndex] },
        });

        setView({
          mode: "prepare",
          id: blobId,
          data: { printData },
          controlsAtom: cAtom,
        });

        setControlsAtom(cAtom);

        worker.terminate();
      }
    };

    worker.postMessage(result);

    workerRef.current = worker;

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [buffer, blobId, setView]);

  if (noExtrusion) {
    return (
      <ViewerOverlayNotice>{t`components.controls.prepare.no-extrusion`}</ViewerOverlayNotice>
    );
  }

  if (!controlsAtom) {
    return (
      <ViewerOverlayNotice title={t`components.controls.prepare.loading-gcode`}>
        <S.ProgressWrapper>
          <ProgressBar
            title={t`components.controls.prepare.loading-file`}
            value={loadProgress}
          />
          <ProgressBar
            title={t`components.controls.prepare.parsing-file`}
            value={parseProgress}
          />
          <ProgressBar
            title={t`components.controls.prepare.constructing-geometry`}
            value={constructionProgress}
          />
          {!!loadingError && t`components.controls.loading-error`}
        </S.ProgressWrapper>
      </ViewerOverlayNotice>
    );
  }

  return (
    <GCodeControls
      controlsAtom={controlsAtom}
      project={project}
      revision={revision}
    />
  );
};

type RevisionResourceLoaderProps = {
  /** Project containing this revision */
  project: Project;

  /** Revision to load resources for */
  revision: Revision;
};

/**
 * Loads the G-code for the given revision into the viewer and renders controls for it.
 */
const RevisionResourceLoader = ({
  project,
  revision,
}: RevisionResourceLoaderProps) => {
  const gcodeBlobRecord = revision?.gcodeFile?.file;
  if (!gcodeBlobRecord) {
    return (
      <ViewerOverlayNotice>{t`components.controls.prepare.no-gcode`}</ViewerOverlayNotice>
    );
  }

  return (
    <GCodeLoader
      blobRecord={gcodeBlobRecord}
      project={project}
      revision={revision}
    />
  );
};

type PrepareViewerControlPanelProps = {
  /**
   * Project to view.
   */
  project: Project;
};

const RevisionNotFoundError = (): ReactElement => (
  <ViewerOverlayNotice
    title={t`common.revision-not-found`}
  >{t`common.revision-not-found-description`}</ViewerOverlayNotice>
);

export const PrepareViewerControlPanel = ({
  project,
}: PrepareViewerControlPanelProps): ReactElement => {
  const [_match, params] = useRoute(projectRoutes.prepare);

  const revisionId = params?.revisionId;

  if (!revisionId) {
    return (
      <ViewerOverlayNotice>{t`components.controls.prepare.no-revisions`}</ViewerOverlayNotice>
    );
  }

  const revision = project.revisions.find((r) => r.id === revisionId);

  if (!revision) {
    return isValidUuid(revisionId) ? (
      <RevisionNotFoundError />
    ) : (
      <BadUrlError />
    );
  }

  return <RevisionResourceLoader project={project} revision={revision} />;
};
